import type { To } from 'react-router';
import { merge } from 'lodash-es';
import React, { useId, useRef } from 'react';
import { useLocation } from 'react-router';

import type { DynamicIconName } from '../../assets/DynamicIcon/DynamicIcon';
import type { VariantProp } from '../../common/colors';
import type { BadgeProps } from '../../components/Badge/Badge';
import type { TooltipProps } from '../../components/Tooltip/Tooltip';
import type { ControlSize } from '../../controls/shared/types';
import type { WidthTypes } from '../../utilities/shared/Width';
import type { WrapProps } from '../../utilities/shared/Wrap';
import type { PolymorphicComponentProps } from '../../utilities/types/polymorphicAsProp';
import { DynamicIcon } from '../../assets/DynamicIcon/DynamicIcon';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { Badge } from '../../components/Badge/Badge';
import { BadgeGroup } from '../../components/Badge/BadgeGroup';
import { Link } from '../../components/Link/Link';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { selectors, transitions } from '../../controls/shared/styles';
import {
  getControlBadgeSize,
  getControlIconSize,
  getControlPreset,
} from '../../controls/shared/vars';
import { darkThemeSelector, shadows, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { displayCSS } from '../../utilities/shared/Display';
import { type MaxWidthTypes, maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { type MinWidthTypes, minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import { wrapCSS } from '../../utilities/shared/Wrap';
import { usePresentation } from '../../utilities/usePresentation';
import { SelectedProvider, useSelected } from '../../utilities/useSelected';

const TargetIcon = styled(DynamicIcon, {
  color: variantIconColor,
});

const TargetLabel = styled(Text, {
  color: variantBodyColor,
});

const TargetContents = styled(AlignStack, {
  position: 'relative',
  zIndex: 2,
});

const TargetBoundary = styled('div', {
  position: 'absolute',
  zIndex: 1,
  top: 'calc($$boundaryEnds * -1)',
  right: 'calc($$boundaryRight * -1)',
  bottom: 'calc($$boundaryEnds * -1)',
  left: 'calc($$boundarySides * -1)',
  background: variantBackgroundColor,
  strokeAll: variantStrokeColor,
  borderRadius: '$$boundaryRadius',
  opacity: '$$boundaryOpacity',
  transition: transitions.control,
});

const TargetContainer = styled('div', {
  position: 'relative',
  display: 'flex',
  maxWidth: '100%',
  verticalAlign: 'bottom',
  transition: transitions.control,
  $$boundaryOpacity: 0,
  ...getVariantStyles('neutral'),

  variants: {
    hasAnnotation: {
      true: {},
      false: {
        $$boundaryRight: '$$boundarySides',
      },
    },
    isPressable: {
      true: {
        cursor: 'pointer',

        [selectors.hover]: {
          $$boundaryOpacity: 0.8,
        },

        [selectors.focus]: {
          outline: 'none',
          $$boundaryOpacity: 0.8,

          [`${TargetBoundary}`]: {
            boxShadow: shadows.focusRingLight,

            [darkThemeSelector]: {
              boxShadow: shadows.focusRingDark,
            },
          },
        },
      },
      false: {
        cursor: 'default',
      },
    },
    // No `selected` axis: selection resolves into `variant` before it reaches this component,
    // so a rule here would only collide with the `variant` axis over the same locals.
    size: {
      'x-small': {
        $$boundaryEnds: '$space$1',
        $$boundarySides: '$space$3',
        $$boundaryRadius: '$space$4',
      },
      small: {
        $$boundaryEnds: '$space$2',
        $$boundarySides: '$space$4',
        $$boundaryRadius: '$space$6',
      },
      medium: {
        $$boundaryEnds: '$space$2',
        $$boundarySides: '$space$6',
        $$boundaryRadius: '$space$8',
      },
      large: {
        $$boundaryEnds: '$space$3',
        $$boundarySides: '$space$8',
        $$boundaryRadius: '$space$8',
      },
      'x-large': {
        $$boundaryEnds: '$space$4',
        $$boundarySides: '$space$12',
        $$boundaryRadius: '$space$10',
      },
    },
    variant: {
      alternative: {
        ...getVariantStyles('alternative'),
      },
      attention: {
        ...getVariantStyles('attention'),
      },
      brand: {
        ...getVariantStyles('brand'),
      },
      negative: {
        ...getVariantStyles('negative'),
      },
      neutral: {
        ...getVariantStyles('neutral'),
      },
      positive: {
        ...getVariantStyles('positive'),
      },
    },
    internal: {
      true: {},
      false: {},
    },
  },

  // Keeps the badge's clearance equal on the top, bottom, and right, with the radius set to the
  // badge's plus that clearance. Only at `small` does the badge stand taller (4px) than the label.
  compoundVariants: [
    {
      hasAnnotation: true,
      size: 'x-small',
      css: {
        $$boundaryRight: '$space$1',
        $$boundaryRadius: '$space$5',
      },
    },
    {
      hasAnnotation: true,
      size: 'small',
      css: {
        $$boundaryEnds: '$space$4',
        $$boundaryRight: '$space$2',
        $$boundaryRadius: '$space$8',
      },
    },
    {
      hasAnnotation: true,
      size: 'medium',
      css: {
        $$boundaryRight: '$space$2',
        $$boundaryRadius: '$space$8',
      },
    },
    {
      hasAnnotation: true,
      size: 'large',
      css: {
        $$boundaryRight: '$space$3',
        $$boundaryRadius: '$space$11',
      },
    },
    {
      hasAnnotation: true,
      size: 'x-large',
      css: {
        $$boundaryRight: '$space$4',
        $$boundaryRadius: '$space$12',
      },
    },
    // A compound variant so it outranks `variant` whatever order Stitches injected them in.
    {
      internal: true,
      css: {
        ...getVariantStyles('neutral', true),
      },
    },
  ],
});

export type TargetPropSize = 'large' | 'medium' | 'small';

export type TargetBaseProps = {
  annotation?: BadgeProps | BadgeProps[];
  'aria-label'?: string;
  children?: React.ReactNode;
  display?: 'block' | 'flex' | 'inline-block' | 'inline-flex';
  icon?: DynamicIconName;
  internal?: boolean;
  label?: React.ReactNode;
  onClick?: (event: any) => void;
  size?: ControlSize;
  selected?: boolean;
  tooltip?: TooltipProps;
  variant?: VariantProp;
  wrap?: 'nowrap' | 'wrap';
  width?: {
    width?: WidthTypes;
    maxWidth?: MaxWidthTypes;
    minWidth?: MinWidthTypes;
  };
};

export type TargetProps<Tag extends React.ElementType = 'span'> = PolymorphicComponentProps<
  Tag,
  TargetBaseProps
>;

export function Target<Tag extends React.ElementType>({
  annotation,
  as = 'span' as Tag,
  children,
  display = 'flex',
  icon,
  internal,
  label,
  onClick,
  selected,
  size = 'medium',
  tooltip,
  variant,
  wrap = 'wrap',
  width,
  ref,
  ...remaining
}: TargetProps<Tag>) {
  const isSelected = useSelected(selected);
  const effectivePreset = getControlPreset(size);
  const effectiveIconSize = getControlIconSize(size);
  const effectiveAnnotationSize = getControlBadgeSize(size);
  const isPressable = Boolean(onClick || (as !== 'span' && as !== 'div'));
  // A pressable target reads as alternative by default, but not when selected: that would
  // give `resolveVariant` a non-neutral variant and block the promotion to brand.
  const pressableFallback = isPressable && !isSelected ? 'alternative' : undefined;
  const { resolvedVariant } = usePresentation({
    variant: variant ?? pressableFallback,
    selected: isSelected,
  });
  const renderedLabel = children ?? label;
  const labelRef = useRef<HTMLSpanElement>(null);
  const isLabelClipped = () => {
    const el = labelRef.current;
    return el != null && el.scrollWidth > el.clientWidth;
  };
  const tooltipVisible = () => tooltip?.contents !== renderedLabel || isLabelClipped();
  const css = merge(
    displayCSS(display),
    widthCSS(width?.width),
    maxWidthCSS(width?.maxWidth),
    minWidthCSS(width?.minWidth),
  );

  const renderAnnotation = Array.isArray(annotation) ? (
    <BadgeGroup size={effectiveAnnotationSize}>
      {annotation.map((item, index) => (
        <Badge key={item.id ?? index} {...item} />
      ))}
    </BadgeGroup>
  ) : (
    <Badge {...annotation} size={effectiveAnnotationSize} />
  );

  const render = (
    <TargetContainer
      ref={ref}
      as={as}
      onClick={onClick}
      role={onClick && 'button'}
      tabIndex={isPressable ? 0 : undefined}
      hasAnnotation={!!annotation}
      internal={internal}
      isPressable={isPressable}
      size={size}
      variant={resolvedVariant}
      css={css}
      {...remaining}
    >
      <SelectedProvider value={isSelected}>
        <TargetContents
          direction="row"
          gap={size === 'large' ? 6 : 4}
          preset={effectivePreset}
          start={
            icon && (
              <TargetIcon
                priority={['framed', 'service', 'provider', 'hardware']}
                icon={icon}
                size={effectiveIconSize}
              />
            )
          }
          end={annotation && renderAnnotation}
        >
          {renderedLabel != null && (
            <TargetLabel
              ref={labelRef}
              truncate={wrap === 'nowrap'}
              weight="bold"
              whitespace={wrap === 'wrap' ? 'normal' : 'no-wrap'}
            >
              {renderedLabel}
            </TargetLabel>
          )}
        </TargetContents>
      </SelectedProvider>
      <TargetBoundary />
    </TargetContainer>
  );

  return tooltip ? (
    <Tooltip {...tooltip} shouldOpen={() => tooltipVisible() && (tooltip.shouldOpen?.() ?? true)}>
      {render}
    </Tooltip>
  ) : (
    render
  );
}

const TargetsToken = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'no-wrap',
  alignItems: 'center',
});

const TargetsContainer = styled('div', {
  flexDirection: 'row',
  flexWrap: 'wrap',
  alignItems: 'center',
  verticalAlign: 'bottom',
  gap: '$6',
});

function TargetsOverflow({
  targets,
  maximumTargets,
  overflow,
}: {
  targets: TargetProps<any>[];
  maximumTargets?: number;
  overflow?: {
    as?: React.ElementType;
    to?: To;
    onClick?: () => void;
  };
}) {
  const overflowCount = maximumTargets ? targets.length - maximumTargets : undefined;
  const location = useLocation();
  const isCurrentPath = overflow?.to && location.pathname === overflow.to;

  let moreControl: React.ReactNode;
  if (overflow?.onClick) {
    moreControl = (
      <Link as="button" type="button" variant="alternative" onClick={overflow.onClick}>
        {overflowCount} more
      </Link>
    );
  } else if (overflow && !isCurrentPath) {
    moreControl = (
      <Link as={overflow?.as} to={overflow?.to}>
        {overflowCount} more
      </Link>
    );
  } else {
    moreControl = <Text>{overflowCount} more</Text>;
  }

  return <Text>and {moreControl}</Text>;
}

export function Targets<Tag extends React.ElementType>({
  display = 'flex',
  hideSeparator = false,
  maximumTargets,
  overflow,
  size = 'medium',
  targets,
  variant,
  wrap = 'wrap',
}: {
  display?: 'block' | 'flex' | 'inline-block' | 'inline-flex';
  hideSeparator?: boolean;
  maximumTargets?: number;
  size?: TargetPropSize;
  variant?: VariantProp;
  targets: TargetProps<Tag>[];
  overflow?: {
    as?: React.ElementType;
    to?: To;
    onClick?: () => void;
  };
} & WrapProps) {
  const id = useId();
  const css = merge(displayCSS(display), wrapCSS(wrap));

  return (
    <TargetsContainer css={css}>
      {targets.slice(0, maximumTargets ?? targets.length).map((target, index) => (
        <TargetsToken key={`${id}-${target['aria-label'] ?? target.label ?? index}`}>
          <Target display={display} size={size} variant={variant} {...target} />
          {index < targets.length - 1 && !hideSeparator && <Text>,</Text>}
        </TargetsToken>
      ))}
      {maximumTargets && targets.length > maximumTargets && (
        <TargetsOverflow targets={targets} maximumTargets={maximumTargets} overflow={overflow} />
      )}
    </TargetsContainer>
  );
}
